import {
  Wallet, TrendingUp, Wrench, Landmark, CreditCard, Lock, Building2,
} from "lucide-react";

export const PHONE_DISPLAY = "(888) 550-9339";
export const PHONE_TEL = "tel:8885509339";

export const SERVICES = [
  {
    slug: "working-capital-loans",
    name: "Working Capital Loans",
    icon: Wallet,
    short: "Cover payroll, inventory, or daily operations with fast access to funds — without disrupting your cash flow.",
    hero: "Keep your business running smoothly.",
    description:
      "Working capital loans give you quick access to funds for payroll, inventory, marketing, or unexpected expenses. Repayment structures are designed around your cash flow so your business stays flexible and responsive to opportunity.",
    bullets: [
      "Funding from $10K to $2M+",
      "Approvals in as soon as 2 hours",
      "Credit scores from 500+ considered",
      "Minimal paperwork — apply in 5 minutes",
    ],
    goodFor: ["Payroll and operating expenses", "Inventory purchases", "Bridging seasonal gaps", "Marketing and growth pushes"],
  },
  {
    slug: "merchant-cash-advance",
    name: "Merchant Cash Advances",
    icon: TrendingUp,
    short: "Funding based on your revenue, with repayments tied to your daily or weekly sales.",
    hero: "Funding that moves with your revenue.",
    description:
      "A merchant cash advance provides capital based on your business revenue. Repayments flex with your daily or weekly sales, making it ideal for businesses with fluctuating income — fast, flexible, and aligned with your cash stream.",
    bullets: [
      "Revenue-based qualification",
      "Same-week funding in most cases",
      "Repayments align with your sales",
      "Bad credit OK — 500+ scores considered",
    ],
    goodFor: ["Restaurants and retail", "Seasonal businesses", "Fast turnaround needs", "Businesses declined by banks"],
  },
  {
    slug: "equipment-financing",
    name: "Equipment Financing",
    icon: Wrench,
    short: "Acquire machinery, vehicles, or technology without depleting your working capital.",
    hero: "Invest in the tools that grow your business.",
    description:
      "From machinery to technology upgrades, equipment financing helps you acquire essential assets while preserving cash. The equipment itself typically secures the loan, and repayment terms are tailored to your cash flow.",
    bullets: [
      "Finance new or used equipment",
      "Preserve working capital",
      "Potential tax advantages (Section 179)",
      "Terms tailored to equipment life",
    ],
    goodFor: ["Construction and heavy machinery", "Commercial vehicles", "Medical and dental equipment", "Restaurant and retail build-outs"],
  },
  {
    slug: "sba-loans",
    name: "SBA & Long-Term Loans",
    icon: Landmark,
    short: "Competitive rates and structured repayment for expansion, property, or refinancing.",
    hero: "Plan for the long game.",
    description:
      "SBA and long-term business loans are designed for expansion, property purchases, or refinancing. They offer competitive interest rates and structured repayment schedules that provide stability while supporting long-term growth goals.",
    bullets: [
      "SBA 7(a) and 504 programs",
      "Lower rates, longer terms",
      "Expansion, acquisition and refi",
      "Expert guidance through the process",
    ],
    goodFor: ["Business expansion", "Owner-occupied real estate", "Debt consolidation and refinancing", "Business acquisitions"],
    partner: {
      label: "Apply through our SBA partner portal",
      url: "https://portal.newitymarket.com/partner/sba/ec-cap",
      note: "For SBA 7(a) small loans, you can also apply directly through our lending partner Newity — pre-filled with our partner code so your application routes back to our team.",
    },
  },
  {
    slug: "business-line-of-credit",
    name: "Business Lines of Credit",
    icon: CreditCard,
    short: "Draw funds as needed and pay interest only on what you use.",
    hero: "Flexibility on standby.",
    description:
      "A business line of credit lets you draw funds whenever you need them. Unlike a traditional loan, you only pay interest on what you use — freedom to manage cash flow, cover surprises, or jump on growth opportunities.",
    bullets: [
      "Revolving access to capital",
      "Interest only on what you draw",
      "Reusable as you repay",
      "Great safety net for cash flow",
    ],
    goodFor: ["Cash-flow management", "Unexpected expenses", "Opportunistic purchases", "Recurring inventory cycles"],
  },
  {
    slug: "secured-business-loans",
    name: "Secured Business Loans",
    icon: Lock,
    short: "Leverage equipment, property, or inventory to unlock larger amounts at better rates.",
    hero: "Put your assets to work.",
    description:
      "By using equipment, property, or inventory as collateral, secured business loans unlock larger capital amounts with lower interest rates and more favorable repayment terms — strategic, secure growth funding.",
    bullets: [
      "Larger loan amounts",
      "Lower rates than unsecured options",
      "Flexible collateral types",
      "Longer repayment terms available",
    ],
    goodFor: ["Major expansions", "Large equipment purchases", "Inventory-heavy businesses", "Rate-sensitive borrowers"],
  },
  {
    slug: "commercial-real-estate-loans",
    name: "Commercial Real Estate Loans",
    icon: Building2,
    short: "SBA 504, conventional, bridge, construction, and multi-family financing.",
    hero: "Financing for the property behind the business.",
    description:
      "Purchase, refinance, or build with commercial real estate financing — SBA 504, conventional, bridge, construction, and multi-family programs, guided by a team that closes on time.",
    bullets: [
      "Purchase, refi, bridge and construction",
      "Multi-family and mixed-use",
      "Competitive LTVs and terms",
      "Closings usually within 7–10 days of clear-to-close",
    ],
    goodFor: ["Owner-occupied purchases", "Investment properties", "Bridge-to-stabilization deals", "Ground-up construction"],
  },
];

export const WHY_US = [
  { title: "Best Rates & Fees", body: "We offer the most competitive pricing — guaranteed." },
  { title: "Flexible Loan Programs", body: "In-house programs plus a deep investor and lender network to fund most scenarios." },
  { title: "Robust Technology", body: "Web-based intake, document upload, e-signatures and live status tracking." },
  { title: "On-Time Quick Closings", body: "We close faster than banks and competitors — usually within 7–10 days." },
  { title: "Easy Application", body: "A smart Quick & Full App that knows exactly what info and docs to ask for." },
  { title: "Dedicated Account Rep", body: "Every borrower is VIP — one rep who always knows your loan status." },
];

export const INDUSTRIES = [
  "Consulting", "Healthcare", "Gas Stations", "Landscaping",
  "Restaurants", "Retail", "Transportation", "Construction",
];

export const PROCESS = [
  { step: "01", title: "Apply in 5 minutes", body: "Online or by phone — minimal paperwork, guided every step." },
  { step: "02", title: "Approved in as soon as 2 hours", body: "Our experts review fast and come back with real options." },
  { step: "03", title: "Funded in days", body: "Funds deposited promptly so you can move on what matters." },
];
